import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Clock, Check, PlayCircle, CalendarClock } from "lucide-react";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import { useI18n } from "@/context/I18nContext";

export default function Assignments() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/my-assignments").then((r) => setData(r.data)).catch(() => setData({ due_soon: [], in_progress: [], completed: [] })); }, []);

  if (!data) return <AppLayout><div className="max-w-3xl mx-auto px-4 py-10"><div className="nb-card h-40 animate-pulse" /></div></AppLayout>;

  const empty = !data.due_soon.length && !data.in_progress.length && !data.completed.length;
  const dueLabel = { overdue: t("assignments.overdue"), due_today: t("assignments.dueToday"), due_soon: t("assignments.dueSoon"), scheduled: t("assignments.scheduled") };
  const dueClass = { overdue: "bg-coral text-white", due_today: "bg-butter", due_soon: "bg-peach", scheduled: "bg-white" };

  return (
    <AppLayout>
      <Seo title="My Assignments · FluentLoop" noindex />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="font-cabinet font-extrabold text-3xl flex items-center gap-2"><GraduationCap className="w-7 h-7" /> {t("assignments.title")}</h1>
        <p className="text-gray-600">{t("assignments.subtitle")}</p>

        {empty && (
          <div className="nb-card bg-butter p-6 mt-6 text-center" data-testid="assignments-empty">
            <p className="font-cabinet font-bold">{t("assignments.empty")}</p>
            <p className="text-sm text-gray-700 mt-1">{t("assignments.emptySub")}</p>
            <Link to="/today" className="nb-btn-primary mt-4 inline-flex px-5 py-2">{t("assignments.doTodayLoop")}</Link>
          </div>
        )}

        <Section title={t("assignments.toDo")} icon={PlayCircle} items={data.due_soon} testid="assign-todo" cta={t("assignments.start")} dueLabel={dueLabel} dueClass={dueClass} doneLabel={t("assignments.done")} lateLabel={t("assignments.late")} />
        <Section title={t("assignments.inProgress")} icon={Clock} items={data.in_progress} testid="assign-inprogress" cta={t("assignments.continue")} dueLabel={dueLabel} dueClass={dueClass} doneLabel={t("assignments.done")} lateLabel={t("assignments.late")} />
        <Section title={t("assignments.completed")} icon={Check} items={data.completed} testid="assign-completed" done dueLabel={dueLabel} dueClass={dueClass} doneLabel={t("assignments.done")} lateLabel={t("assignments.late")} />
      </div>
    </AppLayout>
  );
}

function Section({ title, icon: Icon, items, testid, cta, done, dueLabel, dueClass, doneLabel, lateLabel }) {
  if (!items?.length) return null;
  return (
    <div className="mt-8" data-testid={testid}>
      <h2 className="font-cabinet font-bold text-xl flex items-center gap-2 mb-3"><Icon className="w-5 h-5" /> {title} ({items.length})</h2>
      <div className="space-y-3">
        {items.map((a) => <Card key={a.assignment_id} a={a} cta={cta} done={done} dueLabel={dueLabel} dueClass={dueClass} doneLabel={doneLabel} lateLabel={lateLabel} />)}
      </div>
    </div>
  );
}

function Card({ a, cta, done, dueLabel, dueClass, doneLabel, lateLabel }) {
  const pct = a.total_steps ? Math.round((a.completed_steps / a.total_steps) * 100) : 0;
  return (
    <Link to={`/assignment/${a.assignment_id}`} className={`nb-card p-4 flex items-center justify-between gap-3 hover:-translate-y-0.5 transition-transform ${done ? "bg-mint/40" : "bg-white"}`} data-testid={`assignment-${a.assignment_id}`}>
      <div className="min-w-0">
        <div className="font-cabinet font-bold truncate">{a.title}</div>
        <div className="text-xs text-gray-600 flex flex-wrap items-center gap-2 mt-1">
          <span>{a.class_name}</span>
          <span>· {a.target_language?.toUpperCase()} {a.level}</span>
          {a.estimated_minutes && <span>· ~{a.estimated_minutes} min</span>}
          {a.due_date && <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {new Date(a.due_date).toLocaleDateString()}</span>}
        </div>
        {!done && a.status === "in_progress" && (
          <div className="mt-2 flex items-center gap-2">
            <div className="h-1.5 w-28 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-coral" style={{ width: `${pct}%` }} /></div>
            <span className="text-[11px] text-gray-500">{a.completed_steps}/{a.total_steps}</span>
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        {dueLabel[a.due_state] && !done && <span className={`nb-tag text-xs ${dueClass[a.due_state]}`}>{dueLabel[a.due_state]}</span>}
        {done ? (
          <span className="nb-tag bg-mint text-xs flex items-center gap-1"><Check className="w-3 h-3" /> {doneLabel}{a.completed_after_due ? ` ${lateLabel}` : ""}</span>
        ) : (
          <span className="nb-btn-primary py-1.5 px-3 text-xs">{cta}</span>
        )}
      </div>
    </Link>
  );
}
